'use client';

import { Mail, Phone, MessageCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CONTACT_EMAIL, CONTACT_EMAIL_HREF, CONTACT_PHONE, CONTACT_PHONE_TEL, CONTACT_SMS } from '@/lib/contact';
import { cn } from '@/lib/utils';

/**
 * Floating "talk to a human" widget. Mounts after idle so it never competes
 * with page content for first paint.
 */
export default function HumanContactFloat() {
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let id: number;
    if (w.requestIdleCallback) {
      id = w.requestIdleCallback(() => setReady(true), { timeout: 2500 });
      return () => w.cancelIdleCallback?.(id);
    }
    id = window.setTimeout(() => setReady(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  if (!ready) return null;

  // A phone-handset icon implies calling; without a real line, use a message
  // icon so the affordance matches the (email) channel it actually opens.
  const TriggerIcon = CONTACT_PHONE ? Phone : MessageCircle;

  return (
    <div className="human-float fixed bottom-6 right-6 z-50">
      {/* Expanded panel */}
      {expanded && (
        <div className="glass glass-blur mb-4 w-80 animate-fade-up rounded-card bg-raised/95 p-6 shadow-lift-lg">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="font-display text-lg text-ink">Talk to a real person</h3>
              <p className="mt-1 text-sm text-ink-muted">
                {CONTACT_PHONE
                  ? 'No bots. No phone trees. We pick up in ~12 seconds.'
                  : 'No bots, no scripts — a real advisor who knows your search.'}
              </p>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="p-1 text-ink-faint hover:text-ink-soft"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3">
            {/* Phone/text only when a real number is configured (lib/contact);
                otherwise an honest email channel — never a fabricated number. */}
            {CONTACT_PHONE && CONTACT_PHONE_TEL ? (
              <>
                <a
                  href={CONTACT_PHONE_TEL}
                  className="group flex items-center gap-4 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 transition-colors hover:bg-emerald-500/20"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 transition-transform group-hover:scale-105">
                    <Phone className="h-6 w-6 text-canvas" />
                  </span>
                  <span>
                    <span className="block font-semibold tabular-nums text-emerald-300">
                      {CONTACT_PHONE}
                    </span>
                    <span className="block text-sm text-ink-muted">Call now — we answer fast</span>
                  </span>
                </a>

                {CONTACT_SMS && (
                  <a
                    href={CONTACT_SMS}
                    className="group glass flex items-center gap-4 rounded-xl p-4 transition-colors hover:bg-glass-bright"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-raised transition-transform group-hover:scale-105">
                      <MessageCircle className="h-6 w-6 text-emerald-300" />
                    </span>
                    <span>
                      <span className="block font-semibold text-ink">Text us</span>
                      <span className="block text-sm text-ink-muted">Reply in under 5 minutes</span>
                    </span>
                  </a>
                )}
              </>
            ) : (
              <a
                href={CONTACT_EMAIL_HREF}
                className="group flex items-center gap-4 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 transition-colors hover:bg-emerald-500/20"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 transition-transform group-hover:scale-105">
                  <Mail className="h-6 w-6 text-canvas" />
                </span>
                <span>
                  <span className="block font-semibold text-emerald-300">{CONTACT_EMAIL}</span>
                  <span className="block text-sm text-ink-muted">Email us — a real advisor replies</span>
                </span>
              </a>
            )}
          </div>

          <p className="mt-4 text-center text-xs text-ink-faint">
            Your dedicated advisor knows your search and preferences
          </p>
        </div>
      )}

      {/* Main button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-2 rounded-full bg-emerald-500 text-canvas shadow-glow-emerald transition-all hover:bg-emerald-400',
          expanded ? 'p-4' : 'px-6 py-4'
        )}
        aria-expanded={expanded}
      >
        {expanded ? (
          <TriggerIcon className="h-6 w-6" />
        ) : (
          <>
            <TriggerIcon className="h-5 w-5" />
            <span className="font-semibold">Talk to a human</span>
          </>
        )}
      </button>
    </div>
  );
}
